// ############ IMPORTS GENERAL
// Basic imports
import axios from 'axios';

// QApp imports
import routes_api from './routes_api';
import routes_web from './routes_web';
import { offlineInterceptor } from '../offline_items/offlineInterceptor';
import { isOfflineSupported, isPlannerProjectActionUrl } from '../offline_items/endpointMapper';
import { cacheProjectBoard, handlePlannerProjectAction } from '../offline_items/plannerProjectsOffline';
import { isTokenExpired, decodeJWT, isTokenValidLocally } from '../offline_items/jwtUtils';

// Prod same-origin (`/` or empty) = Cloud Run FE nginx proxies /api → backend.
// Local: REACT_APP_USE_BACKEND_PROD=false → REACT_APP_API_URL_LOCAL.
function resolveApiBaseURL() {
  const useProd =
    String(process.env.REACT_APP_USE_BACKEND_PROD || '')
      .trim()
      .toLowerCase() === 'true';
  if (!useProd) {
    return process.env.REACT_APP_API_URL_LOCAL || 'http://localhost:8081/';
  }
  const prod = String(process.env.REACT_APP_API_URL_PROD ?? '').trim();
  if (prod === '' || prod === '/') {
    return '/';
  }
  return prod;
}

// ############ CLASSES AND INTERCEPTORS
// Create instance for API connection w/ backend
const api = axios.create({
    baseURL: resolveApiBaseURL(),
});

// ##### INTERCEPTOR REQUEST - Add Token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// ##### INTERCEPTOR REQUEST - Offline Handler
api.interceptors.request.use(
  async (config) => {
    if (!navigator.onLine && isPlannerProjectActionUrl(config.url)) {
      try {
        const offlineResponse = await handlePlannerProjectAction(config);
        return Promise.reject({
          response: offlineResponse,
          config,
          isOffline: true
        });
      } catch (error) {
        return Promise.reject(error);
      }
    }

    if (!navigator.onLine && isOfflineSupported(config.url)) {
      try {
        const offlineResponse = await offlineInterceptor.handleRequest(config);
        return Promise.reject({
          response: offlineResponse,
          config,
          isOffline: true
        });
      } catch (error) {
        return Promise.reject(error);
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ##### INTERCEPTOR RESPONSE - Cache Success
api.interceptors.response.use(
  async (response) => {
    if (navigator.onLine && response.status === 200) {
      const method = String(response.config?.method || '').toLowerCase();
      if (method === 'get' && isPlannerProjectActionUrl(response.config?.url)) {
        try {
          await cacheProjectBoard(response.data);
        } catch (error) {
          console.error('Failed to cache project board:', error);
        }
      }
      const params = response.config?.params;
      const url = response.config?.url || '';
      const filteredProjectChildren = params?.project != null
        && (url.includes('/project-columns/') || url.includes('/project-cards/'));
      if (!filteredProjectChildren) {
        await offlineInterceptor.cacheResponse(response.config, response.data);
      }
    }
    return response;
  },
  async (error) => {
    if (error.isOffline && error.response) {
      return Promise.resolve(error.response);
    }

    if (!navigator.onLine && error.config) {
      const cachedData = await offlineInterceptor.getFromCache(error.config);
      if (cachedData) {
        return Promise.resolve(cachedData);
      }
    }

    const originalRequest = error.config;
    
    // Case 1: If error NOT 401 → normal reject
    if (!error.response || error.response.status !== 401 || originalRequest.url === routes_api.accounts_login) {
      return Promise.reject(error);
    }

    // Case 2: If request comes from RefreshToken (already tried to refresh) → logout
    if (originalRequest.url === routes_api.token_refresh) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = routes_web.accounts_login;
      return Promise.reject(error);
    }

    // Case 3: error 401 in another request → try to update RefreshToken
    const isOffline = !navigator.onLine;
    const accessToken = localStorage.getItem('access_token');
    
    // ##### SECURITY: Block refresh if offline
    if (isOffline) {
      // Check if token is expired locally
      const isExpired = accessToken ? isTokenExpired(accessToken) : true;
      
      if (isExpired) {
        // Token expired offline → limited access mode
        // Reject with special flag to allow limited offline access
        return Promise.reject({
          ...error,
          isOffline: true,
          isExpired: true,
          allowLimitedAccess: true,
          message: 'Token expired offline - limited access mode'
        });
      } else {
        // Token valid offline → allow request to proceed with cached data
        // This should be handled by offlineInterceptor, but if it reaches here,
        // reject to prevent unnecessary network attempts
        return Promise.reject({
          ...error,
          isOffline: true,
          message: 'Offline - using cached data'
        });
      }
    }

    // ##### ONLINE: Try to refresh token
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await api.post(routes_api.token_refresh, { refresh: refreshToken });

      localStorage.setItem('access_token', response.data.access);
      originalRequest.headers['Authorization'] = `Bearer ${response.data.access}`;
      return api(originalRequest);
    } catch (refreshError) {
      // ##### DIFFERENTIATE ERROR TYPES
      // Network error → check if token is still valid locally
      if (!refreshError.response || refreshError.response.status === 0) {
        if (accessToken && isTokenValidLocally(accessToken)) {
          // Token still valid locally → allow limited access
          console.warn('Refresh failed but token still valid locally - limited access mode');
          return Promise.reject({
            ...refreshError,
            allowLimitedAccess: true,
            message: 'Network error - using cached token'
          });
        }
      }

      // Authentication error or token invalid → logout
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = routes_web.accounts_login;
      return Promise.reject(refreshError);
    }
  }
);

export default api;
